import React, { useEffect, useMemo, useState } from "react";
import RewardsCard from "@/components/RewardsCard";
import { api } from "@/lib/api";
import { formatFarmCalendarDate } from "@/lib/farmTime";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock3, MapPin, SunMedium, ListTodo, BellRing, Construction, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createOperation, enqueueOperation, getDeviceId, isNetworkAvailable, listOperations, pendingAttendanceStatus, saveOfflineReference, secureCachedFetch, syncPendingOperations } from "@/lib/offlineSync";

function parseGps(gps) {
    if (!gps) return null;
    if (typeof gps === "string" && gps.includes(",")) {
        const [lat, lng] = gps.split(",").map((part) => Number(part.trim()));
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    if (typeof gps === "object") {
        const lat = Number(gps.lat);
        const lng = Number(gps.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    return null;
}

function normalizeAttendanceStatus(raw) {
    const value = String(raw || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
    const aliases = {
        notstarted: "not_started",
        not_started: "not_started",
        shiftstart: "shift_started",
        shift_started: "shift_started",
        started: "shift_started",
        clockedin: "working",
        clocked_in: "working",
        working: "working",
        onbreak: "on_break",
        on_break: "on_break",
        break: "on_break",
        clockedout: "clocked_out",
        clocked_out: "clocked_out",
        clock_out: "clocked_out",
        finished: "finished",
        shift_end: "finished",
        shift_ended: "finished",
    };
    return aliases[value] || "not_started";
}

function attendanceLabel(status, t) {
    return {
        not_started: t("attendance.status.not_started"),
        shift_started: t("attendance.status.shift_started"),
        working: t("attendance.status.working"),
        on_break: t("attendance.status.on_break"),
        clocked_out: t("attendance.status.clocked_out"),
        finished: t("attendance.status.finished"),
    }[status] || t("common.no_data");
}

const CONTROLLED_TEST_EMPLOYEE_CODE = "CODEX-TEST-EMP-20260815-8F4C";
const controlledTestModeEnabled = process.env.REACT_APP_CONTROLLED_ATTENDANCE_TEST_MODE === "1";

export default function EmployeeHomePage() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [home, setHome] = useState(null);
    const [farm, setFarm] = useState(null);
    const [weather, setWeather] = useState(null);
    const [weatherError, setWeatherError] = useState(false);
    const [alertsCount, setAlertsCount] = useState(null);
    const [clockBusy, setClockBusy] = useState(false);
    const [inFlightAction, setInFlightAction] = useState("");
    const [offlineAttendanceStatus, setOfflineAttendanceStatus] = useState(null);

    useEffect(() => {
        if (!user?.id) return;
        secureCachedFetch(user.id, "employee-home", () => api.get("/employee/home").then((r) => r.data))
            .then((result) => setHome(result.data)).catch(() => setHome(null));
        secureCachedFetch(user.id, "farm-summary", () => api.get("/farm").then((r) => r.data))
            .then((result) => setFarm(result.data)).catch(() => setFarm(null));
        secureCachedFetch(user.id, "employee-alert-count", () => api.get("/alerts-unified").then((r) => r.data?.by_severity?.high ?? 0))
            .then((result) => setAlertsCount(result.data))
            .catch(() => setAlertsCount(null));
        listOperations(user.id).then((rows) => {
            const pendingStatus = pendingAttendanceStatus(rows);
            if (pendingStatus !== "not_started") setOfflineAttendanceStatus(pendingStatus);
        }).catch(() => null);
    }, [user?.id]);

    useEffect(() => {
        const gps = parseGps(farm?.gps);
        if (!gps) {
            setWeather(null);
            setWeatherError(true);
            return;
        }
        setWeatherError(false);
        api.get(`/weather?lat=${gps.lat}&lng=${gps.lng}&days=1`)
            .then((r) => setWeather(r.data))
            .catch(() => {
                setWeather(null);
                setWeatherError(true);
            });
    }, [farm]);

    const today = useMemo(() => formatFarmCalendarDate(new Date(), {
        weekday: "long",
        month: "short",
        day: "numeric",
    }), []);

    const employeeId = home?.employee?.id || "";
    const employeeCode = home?.employee?.employee_code || "";
    const controlledTestTime = controlledTestModeEnabled && employeeCode === CONTROLLED_TEST_EMPLOYEE_CODE;
    const attendanceStatus = offlineAttendanceStatus || normalizeAttendanceStatus(home?.attendance_status || "not_started");
    const attendanceTimestamp = home?.attendance_timestamp || home?.attendance?.timestamp || t("common.no_data");

    const area = home?.assigned_area || t("common.not_assigned");
    const shiftStatus = home?.shift_status || t("common.no_data");
    const taskCount = Number(home?.today_task_count ?? 0);
    const taskCompletionPct = Number(home?.task_completion_pct ?? 0);
    const urgentAlerts = alertsCount == null ? t("common.no_data") : alertsCount;

    const weatherSummary = weather?.current
        ? t("employee_home.weather_temperature", { temperature: Math.round(weather.current.temperature_2m), feels: Math.round(weather.current.apparent_temperature) })
        : weatherError
            ? t("employee_home.weather_unavailable")
            : t("common.loading");
    const weatherSub = weather?.current
        ? t("employee_home.weather_detail", { humidity: weather.current.relative_humidity_2m ?? t("common.no_data"), wind: weather.current.wind_speed_10m ?? t("common.no_data") })
        : weatherError
            ? t("employee_home.weather_location_unavailable")
            : t("employee_home.fetching_weather");

    const clockActions = [
        { label: t("attendance.actions.shift_start"), action: "shift_start" },
        { label: t("attendance.actions.clock_in"), action: "clock_in" },
        { label: t("attendance.actions.morning_break"), action: "break_start", breakType: "morning" },
        { label: t("attendance.actions.lunch"), action: "break_start", breakType: "lunch" },
        { label: t("attendance.actions.afternoon_break"), action: "break_start", breakType: "afternoon" },
        { label: t("attendance.actions.break_end"), action: "break_end" },
        { label: t("attendance.actions.clock_out"), action: "clock_out" },
        { label: t("attendance.actions.shift_end"), action: "shift_end" },
    ];

    const enabledActionsByState = {
        not_started: new Set(["shift_start"]),
        shift_started: new Set(["clock_in"]),
        working: new Set(["break_start", "clock_out"]),
        on_break: new Set(["break_end"]),
        clocked_out: new Set(["shift_end"]),
        finished: new Set(),
    };

    const canSubmitClockAction = (action) => {
        const serverAllowed = Array.isArray(home?.attendance_allowed_actions)
            ? new Set((home.attendance_allowed_actions || []).map((item) => String(item || "").toLowerCase()))
            : null;
        const allowed = serverAllowed || enabledActionsByState[attendanceStatus] || enabledActionsByState.not_started;
        return allowed.has(action);
    };

    const submitClock = async (action, breakType = "") => {
        if (clockBusy || inFlightAction === action) {
            return;
        }
        if (!employeeId) {
            toast.error(home?.employee_link_error || t("employee_home.not_linked"));
            return;
        }
        setClockBusy(true);
        setInFlightAction(action);
        try {
            const payload = { action, employee_id: employeeId };
            if (breakType) payload.break_type = breakType;
            // The backend independently requires an explicit local test process,
            // the dedicated code, and its linked test account.  Normal UI users
            // never send this flag and always use trusted server time.
            if (controlledTestTime) payload.controlled_test_time = true;
            const operation = createOperation({
                userId: user?.id,
                deviceId: getDeviceId(),
                module: "attendance",
                action,
                endpoint: "/attendance/clock",
                payload,
            });
            await enqueueOperation(operation);
            const nextStatus = action === "shift_start" ? "shift_started"
                : action === "clock_in" || action === "break_end" ? "working"
                    : action === "break_start" ? "on_break"
                        : action === "clock_out" ? "clocked_out" : "finished";
            setOfflineAttendanceStatus(nextStatus);
            window.dispatchEvent(new CustomEvent("ajfarm:sync-updated"));
            if (!isNetworkAvailable()) {
                toast.info(t("offline.saved_device"));
                return;
            }
            const result = await syncPendingOperations({
                userId: user.id,
                postOperation: (row) => api.post("/offline-sync/operations", row, { timeout: 10000 }),
            });
            if (result.synced) {
                const refreshed = await api.get("/employee/home");
                setHome(refreshed.data);
                await saveOfflineReference(user.id, "employee-home", refreshed.data);
                setOfflineAttendanceStatus(null);
                toast.success(`${user?.name || t("profile.employee")}: ${attendanceLabel(nextStatus, t)}`);
            } else {
                toast.info(t("offline.saved_device"));
            }
            window.dispatchEvent(new CustomEvent("ajfarm:sync-updated"));
        } catch (error) {
            toast.error(error.response?.data?.detail || t("employee_home.clock_failed"));
        } finally {
            setClockBusy(false);
            setInFlightAction("");
        }
    };

    return (
        <div className="space-y-4 pb-6" data-testid="employee-home-page">
            <RewardsCard />
            <Button className="h-12 w-full" variant="outline" onClick={() => navigate("/field/add")} data-testid="employee-open-field-capture">
                {t("offline.capture.title")}
            </Button>
            <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("employee_home.title")}</div>
                <h1 className="text-3xl font-bold tracking-tight">{user?.name || home?.employee?.name || t("profile.employee")}</h1>
                <p className="text-sm text-muted-foreground">{today}</p>
            </div>

            <Card className="p-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <SunMedium className="w-4 h-4 text-primary" />{t("employee_home.weather_summary")}
                </div>
                <div className="mt-3 text-2xl font-bold">{weatherSummary}</div>
                <div className="text-sm text-muted-foreground mt-1">{weatherSub || t("common.no_data")}</div>
            </Card>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoCard label={t("profile.shift_status")} value={shiftStatus} icon={Clock3} />
                <InfoCard label={t("profile.today_tasks")} value={String(taskCount)} icon={ListTodo} sub={t("employee_home.complete_percent", { percent: taskCompletionPct || 0 })} />
                <InfoCard label={t("employee_home.urgent_alerts")} value={String(urgentAlerts)} icon={BellRing} />
                <InfoCard label={t("profile.assigned_area")} value={area} icon={MapPin} />
            </div>

            <Card className="p-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <Construction className="w-4 h-4 text-primary" />{t("employee_home.clocking")}
                </div>
                {controlledTestTime && (
                    <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900" data-testid="controlled-test-time-notice">
                        {t("employee_home.controlled_time")}
                    </div>
                )}
                {home?.employee_link_error && (
                    <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800">
                        {home.employee_link_error}
                    </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span>{t("common.status")}: <span className="text-foreground font-medium">{shiftStatus}</span></span>
                    <span>{t("common.updated")}: <span className="text-foreground font-medium">{attendanceTimestamp}</span></span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span>{t("nav.attendance")}: <span className="text-foreground font-medium">{attendanceLabel(attendanceStatus, t)}</span></span>
                    <span>{employeeCode ? `ID ${employeeCode}` : employeeId ? `ID ${employeeId}` : t("common.no_data")}</span>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {clockActions.map((item) => (
                        <Button
                            key={`${item.action}-${item.breakType || ""}`}
                            variant={item.action.includes("break") ? "outline" : "default"}
                            className="h-14 text-sm font-semibold"
                            onClick={() => submitClock(item.action, item.breakType)}
                            disabled={clockBusy || !employeeId || !canSubmitClockAction(item.action)}
                            data-testid={`clock-${item.action}${item.breakType ? `-${item.breakType}` : ""}`}
                        >
                            {clockBusy && inFlightAction === item.action ? <LoaderCircle className="w-4 h-4 mr-2 animate-spin" /> : null}
                            {item.label}
                        </Button>
                    ))}
                </div>
            </Card>
        </div>
    );
}

function InfoCard({ label, value, icon: Icon, sub }) {
    return (
        <Card className="p-4 min-h-[112px]">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
                    <div className="mt-2 text-lg font-semibold leading-tight">{value || "No data"}</div>
                    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
                </div>
                <Icon className="w-5 h-5 text-primary shrink-0" />
            </div>
        </Card>
    );
}
